import { Text } from "@/components/ui/baseUi/text"
import {
  PayoutAmountCell,
  PayoutBankNameCell,
  PayoutDateCell,
  PayoutIncludedDateRangeCell,
  PayoutStatusCell,
} from "@/modules/finances/financesPayout/components/payoutTableComponents"
import { PayoutColumnToSort } from "@/modules/finances/utils/financeUtils"
import { cn } from "@/modules/lib/utils"
import { ColumnSortingOrder } from "@/modules/types/datatableTypes"
import { HeaderTemplate } from "@/modules/ui/components/dataTables/templates/headerTemplate"
import TransactionEmptyDark from "@images/empty/transactions_empty_dark.svg"
import { word } from "@modules/core/utils/i18n"
import { PayoutItemEntity } from "@modules/finances/financesPayout/entities/payoutEntity"
import { PayoutEnumApi } from "@modules/finances/financesPayout/gateway/payoutApi"
import { usePayouts } from "@modules/finances/financesPayout/hooks/usePayout"
import { useFinanceSidebar } from "@modules/finances/hooks/useFinanceSidebar"
import { HUDatatable } from "@modules/ui/components/dataTables/huDatatable"
import { createColumnHelper } from "@tanstack/react-table"
import { useState } from "react"

type PayoutTableProps = {
  payoutEnumApi: PayoutEnumApi
  className?: string
} & React.ComponentProps<"div">

const columnHelper = createColumnHelper<PayoutItemEntity>()

export function PayoutTable({ payoutEnumApi, className, ...props }: PayoutTableProps) {
  const [selectedPayout, setSelectedPayout] = useState<PayoutItemEntity>()
  const { navigateToPayoutDetails } = useFinanceSidebar()
  const {
    data: payouts,
    handlePreviousPage,
    handleNextPage,
    hasPreviousPage,
    hasNextPage,
    pageSize,
    setPageSize,
    totalElements,
    setSorting,
    sortBy,
    sortOrder,
  } = usePayouts(payoutEnumApi)

  const onSelectedPayout = (e: PayoutItemEntity) => {
    navigateToPayoutDetails(payoutEnumApi, e.id, {
      replaceAllComponents: true,
      onHide: () => {
        setSelectedPayout(undefined)
        return Promise.resolve(true)
      },
    }).then(() => {
      setSelectedPayout(e)
    })
  }

  const columns = [
    columnHelper.accessor("amount", {
      header: () => (
        <HeaderTemplate
          title={word("finances.payoutTable.headerColumn.amout")}
          variant={
            sortBy === PayoutColumnToSort.AMOUNT
              ? sortOrder === ColumnSortingOrder.ASC
                ? "numberSortAsc"
                : "numberSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(PayoutColumnToSort.AMOUNT, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(PayoutColumnToSort.AMOUNT, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: payout } }) => <PayoutAmountCell payout={payout} />,
    }),
    columnHelper.accessor("status", {
      header: () => (
        <HeaderTemplate
          title={word("finances.payoutTable.headerColumn.status")}
          variant={
            sortBy === PayoutColumnToSort.STATUS
              ? sortOrder === ColumnSortingOrder.ASC
                ? "stringSortAsc"
                : "stringSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(PayoutColumnToSort.STATUS, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(PayoutColumnToSort.STATUS, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: payout } }) => <PayoutStatusCell payout={payout} />,
    }),
    columnHelper.accessor("bankName", {
      header: () => (
        <HeaderTemplate
          title={word("finances.payoutTable.headerColumn.externalAccount")}
          variant={
            sortBy === PayoutColumnToSort.BANK_NAME
              ? sortOrder === ColumnSortingOrder.ASC
                ? "stringSortAsc"
                : "stringSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(PayoutColumnToSort.BANK_NAME, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(PayoutColumnToSort.BANK_NAME, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: payout } }) => <PayoutBankNameCell payout={payout} />,
    }),
    columnHelper.accessor("createdAt", {
      header: () => (
        <HeaderTemplate
          title={word("finances.payoutTable.headerColumn.launched")}
          variant={
            sortBy === PayoutColumnToSort.LAUNCHED
              ? sortOrder === ColumnSortingOrder.ASC
                ? "numberSortAsc"
                : "numberSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(PayoutColumnToSort.LAUNCHED, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(PayoutColumnToSort.LAUNCHED, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: payout } }) => <PayoutDateCell payout={payout} />,
    }),
    columnHelper.accessor("intervalStartDate", {
      header: () => <HeaderTemplate title={word("finances.payoutTable.headerColumn.includedRange")} />,
      cell: ({ row: { original: payout } }) => <PayoutIncludedDateRangeCell payout={payout} />,
    }),
  ]

  return (
    <div className={cn("flex w-full grow flex-col", className)} {...props}>
      {payouts.length <= 0 ? (
        <PayoutEmptyView />
      ) : (
        <HUDatatable
          data={payouts}
          columns={columns}
          handleRowSelectionChange={(row: PayoutItemEntity) => onSelectedPayout(row)}
          selectedRowIndex={payouts.findIndex((item) => item.id === selectedPayout?.id)}
          pageSize={pageSize}
          setPageSize={setPageSize}
          handlePreviousPage={handlePreviousPage}
          handleNextPage={handleNextPage}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          totalElements={totalElements}
          className="*:table-fixed"
        />
      )}
    </div>
  )
}

function PayoutEmptyView() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-4xl bg-surface-background-secondary px-4 py-6">
      <img src={TransactionEmptyDark} alt="Payout transaction Empty Data" className="max-w-[60%]" />
      <Text fontStyle="HS">{word("finances.payoutEmpty.header")}</Text>
      <Text fontStyle="BM" className="text-center wrap-break-word whitespace-break-spaces text-textDefault-heading">
        {word("finances.payoutEmpty.subHeader")}
      </Text>
    </div>
  )
}
